'use client';

import {
	BASE_PRECISION_EXP,
	BigNum,
	QUOTE_PRECISION_EXP,
	SpotBalanceType,
} from '@velocity-exchange/sdk';
import {
	MAIN_POOL_ID,
	UIMarket,
	USDT_SPOT_MARKET_INDEX,
	matchEnum,
	NumLib,
} from '@velocity-exchange/common';
import { usePathname, useRouter } from 'next/navigation';
import { useCallback, useMemo, useState } from 'react';
import { PRIORITY_SPOT_MARKETS } from 'src/environmentVariables/EnvironmentVariables';
import useVelocityClient from 'src/hooks/useVelocityClient';
import useVelocityClientIsReady from 'src/hooks/useVelocityClientIsReady';
import useGoToRoute from 'src/hooks/useGoToRoute';
import useHighlightedSpotMarkets from 'src/hooks/useHighlightedSpotMarkets';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import useVelocityAccountStore, {
	AccountSpotBalance,
} from 'src/stores/useVelocityAccountsStore';
import useFeatureFlagStore from 'src/stores/featureFlag/useFeatureFlagStore';
import { getTotalIsolatedDepositsForUser } from 'src/stores/VelocityStore/helpers/accountDataHelpers';
import { isAmplifyAccountName } from 'src/utils/amplify';
import { cj, cm } from 'src/utils/classNames';
import { useShallow } from 'zustand/react/shallow';
import useBanksToRender from '../../hooks/useBanksToRender';
import { useSwitchMarket } from '../../hooks/velocityActions/market/useSwitchMarket';
import useWalletIsConnected from '../../hooks/useWalletIsConnected';
import AssetBalanceCardV2 from '../AssetBalanceCardV2';
import TableStateWrapper from '../TableStateWrapper';
import ContentGuard from '../ContentGuard';
import BalanceTableSummary from './BalanceTableSummary';
import useModal from 'src/components/Modals/hooks/useModal';
import { BalanceDisplayType } from 'src/@types/types';
import { MOBILE_TABLE_CARD_LIST_CLASSNAME } from 'src/components/Tables/cards/consts';
import usePortfolioEarnData from 'src/components/PortfolioPage/PortfolioEarnSubPage/hooks/usePortfolioEarnData';
import { DataTable } from 'src/components/primitives/Table/DataTable';
import { getBalancesColumns } from './balancesColumns';
import { BalanceRowData, BalancesTableMeta } from './cells/types';
import MobileTableCard from 'src/components/Tables/cards/MobileTableCard';

/**
 * This prop is the final data we need collected for each Balance Row
 */
export type UserBalanceInfo = {
	highlighted: boolean;
	spotMarketIndex: number;
	symbol: string;
	depositsBase: BigNum;
	depositsQuote: BigNum;
	borrowsBase: BigNum;
	borrowsQuote: BigNum;
	netBase: BigNum;
	netQuote: BigNum;
	liqPrice: BigNum;
	accountNum: number;
	assetWeight: BigNum;
	liabilityWeight: BigNum;
	defaultAssetWeight: number;
	defaultLiabilityWeight: number;
	isLoweredAssetWeight: boolean;
	scaleInitialAssetWeightStart: BigNum;
	poolId: number;
};

/**
 * This is the subset of balance info we need in the parent, to avoid any wasteful calculation and rendering when the value hasn't changed
 */
type _UserBalanceInfo = {
	highlighted: boolean;
	spotMarketIndex: number;
	borrowsQuote: BigNum;
	netQuote: BigNum;
	netBase: BigNum;
	depositsQuote: BigNum;
	base90DayEarning: BigNum | undefined;
	quote90DayEarning: BigNum | undefined;
};

const EMPTY_MESSAGE = 'No balances found';
const EMPTY_BALANCES: AccountSpotBalance[] = [];

const UserBalancesPortfolioTable = (props: {
	expanded?: boolean;
	className?: string;
	hideSummary?: boolean;
	/**
	 * Show deposit button if user has no balances
	 */
	showDepositButtonWhenEmpty?: boolean;
	location: 'trade-page' | 'portfolio-page';
}) => {
	const isMobile = useIsMobileScreenSize();
	const connected = useWalletIsConnected();

	const currentUserClient = useVelocityAccountStore(
		(s) => s.getCurrentUserAccount()?.client
	);
	const currentUserAccount =
		connected && currentUserClient?.isSubscribed
			? currentUserClient?.getUserAccount()
			: undefined;

	const poolId = currentUserAccount?.poolId || 0;
	const borrowLendData = useVelocityStore((s) => s.borrowLendData);

	const currentUserKey = useVelocityAccountStore(
		useShallow((s) => s.currentUserKey)
	);
	const currentUserName =
		useVelocityAccountStore(
			useShallow((s) => s.accounts[currentUserKey ?? '']?.name)
		) ?? '';
	const currentUserPoolId = useVelocityAccountStore(
		useShallow((s) => s.accounts[currentUserKey ?? '']?.poolId)
	);

	const spotPositions = useMemo(() => {
		return currentUserClient?.getActiveSpotPositions();
	}, [currentUserClient]);

	// `?? []` would allocate a fresh array on every call, so the selector could
	// never return a stable reference.
	const accountBalances = useVelocityAccountStore(
		(s) => s.getCurrentUserAccount()?.spotBalances ?? EMPTY_BALANCES
	);
	const velocityClientIsReady = useVelocityClientIsReady();
	const velocityClient = useVelocityClient();
	const router = useRouter();
	const pathname = usePathname();
	const goToRoute = useGoToRoute();
	const { switchMarket } = useSwitchMarket();
	const { show } = useModal();
	const isPortfolioPage = props.location === 'portfolio-page';
	const isSwapPage = pathname?.includes('swap');
	const [swapFromMarketIndex, swapToMarketIndex] = useVelocityStore(
		useShallow((s) => [s.swap.fromMarketIndex, s.swap.toMarketIndex])
	);
	const [balanceDisplayType, setBalanceDisplayType] =
		useState<BalanceDisplayType>('base');
	const toggleBalanceDisplayType = useCallback(
		() =>
			setBalanceDisplayType((prev) => (prev === 'base' ? 'notional' : 'base')),
		[]
	);
	const {
		data: { marketIndexTo90DayEarning },
	} = usePortfolioEarnData();

	const highlightedSpotMarkets = useHighlightedSpotMarkets();

	const shouldUseSpecialHighlighting = props.location === 'portfolio-page';

	const sortedBanks = useBanksToRender({
		poolId,
		sortBy: 'accountBalance',
		userKey: currentUserKey,
		opts: {
			highlightType: shouldUseSpecialHighlighting
				? 'forceDespiteBalance'
				: 'higherPriorityIfZeroBalance',
			highlightBanks: highlightedSpotMarkets,
			prioritySort: PRIORITY_SPOT_MARKETS,
		},
	});

	const displayData: {
		balances: _UserBalanceInfo[];
		totalNetQuote: BigNum;
		totalDepositsQuote: BigNum;
		totalBorrowsQuote: BigNum;
	} = useMemo(() => {
		if (
			!velocityClientIsReady ||
			!accountBalances ||
			!currentUserClient?.isSubscribed
		) {
			return {
				balances: [],
				totalNetQuote: BigNum.zero(QUOTE_PRECISION_EXP),
				totalDepositsQuote: BigNum.zero(QUOTE_PRECISION_EXP),
				totalBorrowsQuote: BigNum.zero(QUOTE_PRECISION_EXP),
			};
		}

		const isolatedDepositsBigNum = currentUserClient?.isSubscribed
			? BigNum.from(
					getTotalIsolatedDepositsForUser(currentUserClient),
					QUOTE_PRECISION_EXP
				)
			: BigNum.zero(QUOTE_PRECISION_EXP);

		const balancesByBank: _UserBalanceInfo[] = sortedBanks.map(
			(sortedBankInfo) => {
				const bank = sortedBankInfo.market;

				const depositBalances = accountBalances.filter(
					(bal) =>
						bal.asset.marketIndex === bank.marketIndex &&
						matchEnum(bal.balanceType, SpotBalanceType.DEPOSIT)
				);
				const borrowBalances = accountBalances.filter(
					(bal) =>
						bal.asset.marketIndex === bank.marketIndex &&
						matchEnum(bal.balanceType, SpotBalanceType.BORROW)
				);

				const isUsdcMarket = bank.marketIndex === USDT_SPOT_MARKET_INDEX;

				const depositsBase = NumLib.sumBigNums(
					depositBalances.map((depos) => depos.balance),
					bank.precisionExp
				).add(
					isUsdcMarket ? isolatedDepositsBigNum : BigNum.zero(bank.precisionExp)
				);

				const depositsQuote = NumLib.sumBigNums(
					depositBalances.map((depos) => depos.quoteValue),
					QUOTE_PRECISION_EXP
				).add(
					isUsdcMarket
						? isolatedDepositsBigNum
						: BigNum.zero(QUOTE_PRECISION_EXP)
				);
				const borrowsBase = NumLib.sumBigNums(
					borrowBalances.map((depos) => depos.balance),
					bank.precisionExp
				);
				const borrowsQuote = NumLib.sumBigNums(
					borrowBalances.map((depos) => depos.quoteValue),
					QUOTE_PRECISION_EXP
				);

				const { base90DayEarning, quote90DayEarning } = (() => {
					if (!marketIndexTo90DayEarning) {
						return {
							base90DayEarning: undefined,
							quote90DayEarning: undefined,
						};
					}

					const base90DayEarningRaw = marketIndexTo90DayEarning?.get(
						bank.marketIndex
					)?.base;
					const base90DayEarningBigNum = base90DayEarningRaw
						? BigNum.fromPrint(
								base90DayEarningRaw.toString(),
								BASE_PRECISION_EXP
							)
						: BigNum.zero(BASE_PRECISION_EXP);

					const quote90DayEarningRaw = marketIndexTo90DayEarning?.get(
						bank.marketIndex
					)?.quote;
					const quote90DayEarningBigNum = quote90DayEarningRaw
						? BigNum.fromPrint(
								quote90DayEarningRaw.toString(),
								QUOTE_PRECISION_EXP
							)
						: BigNum.zero(QUOTE_PRECISION_EXP);

					return {
						base90DayEarning: base90DayEarningBigNum,
						quote90DayEarning: quote90DayEarningBigNum,
					};
				})();

				return {
					highlighted:
						(shouldUseSpecialHighlighting && sortedBankInfo.highlighted) ??
						false,
					spotMarketIndex: bank.marketIndex,
					depositsQuote,
					borrowsQuote,
					netQuote: depositsQuote.sub(borrowsQuote),
					netBase: depositsBase.sub(borrowsBase),
					base90DayEarning,
					quote90DayEarning,
				};
			}
		);

		const sortedBalancesByBank = [...balancesByBank].sort((a, b) => {
			const aHighligted = a.highlighted;
			const bHighligted = b.highlighted;

			if (aHighligted && !bHighligted) {
				return -1;
			} else if (!aHighligted && bHighligted) {
				return 1;
			}

			if (a.netQuote.isNeg() && b.netQuote.isNeg()) {
				return a.netQuote.gt(b.netQuote) ? 1 : -1;
			} else if (a.netQuote.isNeg()) {
				return b.netQuote.eqZero() ? -1 : a.netQuote.gt(b.netQuote) ? -1 : 1;
			} else if (b.netQuote.isNeg()) {
				return a.netQuote.eqZero() ? 1 : a.netQuote.gt(b.netQuote) ? -1 : 1;
			}

			// we add a base check because some dust amounts are so small they have 0 quote when shifted for precision,
			// but using base we should ofc rank them higher than 0 base balances
			if (a.netBase.gtZero() && b.netBase.eqZero()) {
				return -1;
			} else if (b.netBase.gtZero() && a.netBase.eqZero()) {
				return 1;
			}

			return a.netQuote.eq(b.netQuote) ? 0 : a.netQuote.gt(b.netQuote) ? -1 : 1;
		});

		// Include non-zero balances or balances with 90 day earning > $0.01 on portfolio page.
		const filteredBalances = sortedBalancesByBank.filter(
			(bal) =>
				!bal.netBase.eqZero() ||
				(isPortfolioPage &&
					bal.quote90DayEarning &&
					bal.quote90DayEarning.toNum() > 0.01)
		);

		const totalNetQuote = NumLib.sumBigNums(
			filteredBalances?.map(
				(row) => row?.netQuote ?? BigNum.zero(QUOTE_PRECISION_EXP)
			) ?? [],
			QUOTE_PRECISION_EXP
		);

		const totalDepositsQuote = NumLib.sumBigNums(
			filteredBalances?.map(
				(row) => row?.depositsQuote ?? BigNum.zero(QUOTE_PRECISION_EXP)
			) ?? [],
			QUOTE_PRECISION_EXP
		);

		const totalBorrowsQuote = NumLib.sumBigNums(
			filteredBalances?.map(
				(row) => row?.borrowsQuote ?? BigNum.zero(QUOTE_PRECISION_EXP)
			) ?? [],
			QUOTE_PRECISION_EXP
		);

		return {
			balances: filteredBalances,
			totalNetQuote,
			totalDepositsQuote,
			totalBorrowsQuote,
		};
	}, [
		accountBalances,
		currentUserAccount?.totalDeposits?.toString(),
		currentUserAccount?.totalWithdraws?.toString(),
		currentUserClient,
		sortedBanks,
		spotPositions,
		borrowLendData,
		velocityClient,
		isPortfolioPage,
	]);
	const isIsolatedPoolAccount =
		currentUserPoolId != undefined && currentUserPoolId !== MAIN_POOL_ID;
	const isAmplifyAccount = isAmplifyAccountName(currentUserName);

	const balancesColumns = useMemo(
		() => getBalancesColumns({ isAmplifyAccount, location: props.location }),
		[isAmplifyAccount, props.location]
	);

	const dataTableData: BalanceRowData[] = useMemo(
		() =>
			displayData.balances.map((bal) => ({
				spotMarketIndex: bal.spotMarketIndex,
				highlighted: bal.highlighted,
				netQuote: bal.netQuote,
				netBase: bal.netBase,
				depositsQuote: bal.depositsQuote,
				borrowsQuote: bal.borrowsQuote,
				base90DayEarning: bal.base90DayEarning,
				quote90DayEarning: bal.quote90DayEarning,
			})),
		[displayData.balances]
	);

	const shownSpotMarkets = useFeatureFlagStore(
		(s) => s.flags.SHOWN_SPOT_MARKETS.markets
	);
	const isShownSpotMarketsEnabled = useFeatureFlagStore((s) =>
		s.flagEnabled('SHOWN_SPOT_MARKETS')
	);

	const handleSelection = useCallback(
		(marketIndex: number) => {
			const uiMarket = UIMarket.createSpotMarket(marketIndex);

			const spotMarketIsHidden = isShownSpotMarketsEnabled
				? !shownSpotMarkets.includes(uiMarket.uiSymbols.marketSymbol)
				: false;

			if (isSwapPage || spotMarketIsHidden) {
				if (uiMarket.isUsdtMarket) {
					const isFromUsdc =
						UIMarket.createSpotMarket(swapFromMarketIndex).isUsdtMarket;
					const isToUsdc =
						UIMarket.createSpotMarket(swapToMarketIndex).isUsdtMarket;
					if (isFromUsdc || isToUsdc) {
						return;
					} else {
						goToRoute(
							`/swap/USDC-${
								UIMarket.createSpotMarket(swapToMarketIndex).uiSymbols
									.marketSymbol
							}`
						);
					}
				} else {
					goToRoute(`/swap/USDC-${uiMarket.market.symbol}`);
				}
			} else {
				if (uiMarket.isUsdtMarket) {
					return;
				}
				switchMarket({
					marketIndex: uiMarket.market.marketIndex,
					marketType: uiMarket.marketType,
				});
				goToRoute(`/${uiMarket.uiSymbols.baseAssetSymbol}`);
			}
		},
		[
			router,
			isSwapPage,
			goToRoute,
			swapFromMarketIndex,
			swapToMarketIndex,
			shownSpotMarkets.length,
			isShownSpotMarketsEnabled,
		]
	);

	const tableMeta: BalancesTableMeta = useMemo(
		() => ({
			handleSelection,
			isAmplifyAccount,
			isPortfolioPage,
		}),
		[handleSelection, isAmplifyAccount, isPortfolioPage]
	);

	const handleClickDepositButton = useCallback(() => {
		show({
			type: 'deposit',
			props: {
				defaultLargestBalance: true,
			},
		});
	}, [currentUserKey, show]);

	if (isMobile) {
		return (
			<TableStateWrapper
				records={displayData.balances}
				emptyStateText={'No balances found'}
				requireWalletConnect
				requireAccountCreated
				id="user_balances_portfolio_table"
				showDepositButtonWhenEmpty={props.showDepositButtonWhenEmpty}
				handleClickDepositButton={handleClickDepositButton}
				className="thin-scroll overflow-x-auto pb-0"
				innerClassName={cj(
					(!connected || displayData.balances?.length === 0) &&
						'flex min-h-40 items-center'
				)}
			>
				<div
					className={cm(
						'w-full',
						MOBILE_TABLE_CARD_LIST_CLASSNAME,
						props.location === 'trade-page' && 'pt-md',
						props.location === 'portfolio-page' && 'px-0',
						props.className
					)}
				>
					{connected && props.location === 'trade-page' && (
						<BalanceTableSummary netValue={displayData.totalNetQuote} />
					)}

					{displayData.balances.length > 0 ? (
						displayData.balances.map((userBalanceInfo, index) => (
							<AssetBalanceCardV2
								spotMarketIndex={userBalanceInfo.spotMarketIndex}
								highlighted={userBalanceInfo.highlighted}
								balanceDisplayType={balanceDisplayType}
								onToggleBalanceDisplayType={toggleBalanceDisplayType}
								key={`${index}_${userBalanceInfo.spotMarketIndex}`}
								isPortfolioPage={isPortfolioPage}
								base90DayEarning={userBalanceInfo.base90DayEarning}
								quote90DayEarning={userBalanceInfo.quote90DayEarning}
							/>
						))
					) : (
						<MobileTableCard.EmptyPlaceholder>
							{EMPTY_MESSAGE}
						</MobileTableCard.EmptyPlaceholder>
					)}
				</div>
			</TableStateWrapper>
		);
	}

	return (
		<ContentGuard
			requireWallet
			requireAccount
			isLoading={!velocityClientIsReady}
		>
			<div className={cm('flex h-full w-full flex-col', props.className)}>
				{!isIsolatedPoolAccount && props.location === 'trade-page' && (
					<BalanceTableSummary netValue={displayData.totalNetQuote} />
				)}
				<div className="min-h-0 flex-1">
					<DataTable
						columns={balancesColumns}
						data={dataTableData}
						emptyMessage={EMPTY_MESSAGE}
						columnPinning={{ right: ['actions'] }}
						meta={tableMeta}
						dataTestId="user-balances-table"
					/>
				</div>
			</div>
		</ContentGuard>
	);
};

export default UserBalancesPortfolioTable;
